import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, ChevronRight, FileWarning } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import Button from '../components/ui/Button';
import staffRecordService, { errorText } from '../services/staffRecords';
import { WARNING_LEVEL_LABEL, type MyWarning } from '../types/staffRecords';
import { formatDateTimeInTz, useOrgTimezone } from '../utils/timezone';

/**
 * The warnings an employer has sent this person in FactreeTech
 * (`/my-warnings`, `/my-warnings/:id`). No licence gate: it is their own
 * record, and the server scopes it to them.
 *
 * Acknowledging sends back the fingerprint of the text on screen, so what is
 * acknowledged is what was read. It is not agreeing, and the page says so.
 */
export default function MyWarnings() {
  const { id } = useParams<{ id: string }>();
  const warningId = id ? Number(id) : null;
  return warningId ? <OneWarning id={warningId} /> : <WarningList />;
}

function WarningList() {
  const tz = useOrgTimezone();
  const [rows, setRows] = useState<MyWarning[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    staffRecordService.myWarnings()
      .then(list => { if (!cancelled) setRows(list); })
      .catch(err => { if (!cancelled) setError(errorText(err, 'Could not load your warnings.')); });
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <PageHeader
        title="My warnings"
        subtitle="Warnings your employer has sent you, to read and acknowledge."
        icon={<FileWarning className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {!rows && !error && <p className="text-sm text-neutral-500">Loading…</p>}
        {rows && rows.length === 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-6 text-sm text-neutral-600">
            You have no warnings.
          </div>
        )}
        {rows && rows.length > 0 && (
          <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
            {rows.map(w => (
              <li key={w.id}>
                <Link to={`/my-warnings/${w.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 min-w-0">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-neutral-900 break-words">{w.subject}</p>
                    <p className="text-xs text-neutral-500">
                      {WARNING_LEVEL_LABEL[w.level]} · {formatDateTimeInTz(w.issued_at, tz)}
                    </p>
                  </div>
                  <StatusChip status={w.status} />
                  <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function StatusChip({ status }: { status: MyWarning['status'] }) {
  if (status === 'issued') return <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800 shrink-0">To acknowledge</span>;
  if (status === 'acknowledged') return <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800 shrink-0">Acknowledged</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs bg-neutral-200 text-neutral-600 shrink-0">Withdrawn</span>;
}

function OneWarning({ id }: { id: number }) {
  const tz = useOrgTimezone();
  const [w, setW] = useState<MyWarning | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [response, setResponse] = useState('');
  const [read, setRead] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setW(await staffRecordService.myWarning(id));
    } catch (err) {
      setError(errorText(err, 'Could not load this warning.'));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async () => {
    if (!w) return;
    setBusy(true);
    setSaveError(null);
    try {
      setW(await staffRecordService.acknowledge(w.id, {
        hash: w.issued_hash, name: name.trim(), response: response.trim() || null,
      }));
    } catch (err) {
      setSaveError(errorText(err, 'That did not work. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={w ? WARNING_LEVEL_LABEL[w.level] : 'Warning'}
        subtitle={w ? `Issued ${formatDateTimeInTz(w.issued_at, tz)}${w.issued_by_name ? ` by ${w.issued_by_name}` : ''}` : undefined}
        icon={<FileWarning className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <Link to="/my-warnings" className="text-sm text-primary-700 hover:underline">All my warnings</Link>
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {!w && !error && <p className="text-sm text-neutral-500">Loading…</p>}

        {w && w.status === 'withdrawn' && (
          <div className="p-4 bg-neutral-100 border border-neutral-200 rounded-lg text-sm text-neutral-700 break-words">
            This warning was withdrawn{w.withdrawn_at && ` on ${formatDateTimeInTz(w.withdrawn_at, tz)}`}.
            {w.withdrawn_reason && <> Reason: {w.withdrawn_reason}</>}
          </div>
        )}

        {w && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
            <pre className="whitespace-pre-wrap break-words font-sans text-sm text-neutral-900 leading-relaxed">{w.issued_text}</pre>
            <p className="text-xs text-neutral-500 break-all">Fingerprint (SHA-256): {w.issued_hash}</p>
          </div>
        )}

        {w && w.status === 'acknowledged' && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-900 space-y-1 min-w-0">
            <p className="flex items-start gap-2 break-words">
              <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                You acknowledged this warning as {w.acknowledged_name}
                {w.acknowledged_at && `, ${formatDateTimeInTz(w.acknowledged_at, tz)}`}
                {w.acknowledged_method === 'in_person' ? ', on paper' : ''}.
              </span>
            </p>
            {w.employee_response && (
              <p className="whitespace-pre-wrap break-words"><span className="font-medium">Your response: </span>{w.employee_response}</p>
            )}
          </div>
        )}

        {w && w.status === 'issued' && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
            <h2 className="text-base font-semibold text-neutral-900">Acknowledge</h2>
            <p className="text-sm text-neutral-600">
              Acknowledging means you have received and read this warning. It does not mean you agree with it.
              If you want to say something about it, write it below; it is kept with the warning and your employer reads it.
            </p>
            <div>
              <label htmlFor="ack-response" className="block text-sm font-medium text-neutral-700 mb-1">Your response (optional)</label>
              <textarea id="ack-response" rows={4} maxLength={5000}
                className="w-full min-w-0 border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                value={response} onChange={e => setResponse(e.target.value)} />
            </div>
            <div>
              <label htmlFor="ack-name" className="block text-sm font-medium text-neutral-700 mb-1">Your full name</label>
              <input id="ack-name" className="w-full min-w-0 border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                value={name} onChange={e => setName(e.target.value)} />
            </div>
            <label className="flex items-start gap-2 text-sm text-neutral-700 cursor-pointer select-none">
              <input type="checkbox" checked={read} onChange={e => setRead(e.target.checked)}
                className="h-4 w-4 mt-0.5 rounded border-neutral-300 text-primary-600" />
              <span>I have read this warning.</span>
            </label>
            {saveError && <p className="text-sm text-red-600 break-words">{saveError}</p>}
            <Button disabled={busy || !read || name.trim().length < 2} onClick={acknowledge}>
              {busy ? 'Saving…' : 'Acknowledge'}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
